import React from 'react'
import { cn } from '@/lib/utils'

interface ConditionalCheckboxFieldProps {
  label: string
  description?: string
  checked: boolean
  onChange: (checked: boolean) => void
  activeBadge?: string
  children?: React.ReactNode
  className?: string
}

export const ConditionalCheckboxField: React.FC<ConditionalCheckboxFieldProps> = ({
  label,
  description,
  checked,
  onChange,
  activeBadge,
  children,
  className = '',
}) => {
  return (
    <div
      className={cn(
        'border rounded-lg p-4 transition-colors',
        checked ? 'bg-accent/5 border-accent/30' : 'bg-card border-border',
        className,
      )}
    >
      <label className="flex items-start gap-3 cursor-pointer select-none">
        {/* Hidden real checkbox for accessibility */}
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="sr-only"
        />

        {/* Custom visual checkbox */}
        <div className="mt-0.5 flex-shrink-0">
          <div
            className={cn(
              'h-4 w-4 rounded border-2 flex items-center justify-center transition-all',
              checked ? 'bg-accent border-accent' : 'bg-card border-border',
            )}
          >
            {checked && (
              <svg className="h-2.5 w-2.5 text-white" fill="none" viewBox="0 0 12 12">
                <path
                  d="M2 6l3 3 5-5"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>
        </div>

        {/* Label content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-foreground">{label}</span>
            {checked && activeBadge && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-accent text-white">
                {activeBadge}
              </span>
            )}
          </div>
          {description && (
            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{description}</p>
          )}
        </div>
      </label>

      {/* Conditional children */}
      {checked && children && (
        <div className="mt-4 pl-7 space-y-4 border-l-2 border-accent/30">
          {children}
        </div>
      )}
    </div>
  )
}
